import { Grid, Typography, debounce } from "@mui/material";
import { useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../app/hooks";
import CustomAutocompleteMulti from "../../../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import DialogComponent from "../../../../../common/components/functional/dialogs/DialogComponent";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import FormTable, {
  DataItem,
} from "../../../../../common/components/functional/formTable/FormTable";
import { LanguageSkillEdit } from "../../../../../common/components/functional/languageSkillEdit/LanguageSkillEdit";
import { AutocompleteType } from "../../../../../common/types/autoComplete";
import useLanguageSkills from "../../../../../hooks/useLanguageSkills";
import { ContentKey, EditMode } from "../../../../talentProfile/TalentDetails";
import { LanguageSkillCode } from "../../../../talents/talentAPI";
import { LanguageSkill } from "../../../orderTypes";
import { FormTitleGrid } from "../../../styles";

type Language = {
  languageCode: string;
  skillLevel: string;
};

interface Options {
  certificates: AutocompleteType[];
  trainings: AutocompleteType[];
}

const initialSearchTerms = {
  skills: "",
  certificates: "",
  languages: "",
};

export const Requirements = () => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();

  const { getLanguageSkillsNameByCode } = useLanguageSkills();
  const { languages, certificates, trainings, isReadOnly } = watch();
  const localized = useAppSelector((state) => state.localized);
  const { certificate: allCertificateOptions, training: allTrainingOptions } =
    localized;
  const certificateOptions = Object.entries(allCertificateOptions).map(
    ([key, value]) => ({
      id: key,
      name: value,
    }),
  );
  const trainingOptions = Object.entries(allTrainingOptions).map(
    ([key, value]) => ({
      id: key,
      name: value,
    }),
  );

  const [fieldOptions, setFieldOptions] = useState<Options>({
    certificates: certificateOptions,
    trainings: trainingOptions,
  });
  const [searchTerms, setSearchTerms] = useState(initialSearchTerms);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageSkill>();
  const existingLanguageCodes =
    languages.map((language: Language) => language) || [];

  const [editMode, setEditMode] = useState<EditMode>({
    enabled: false,
    content: null,
    id: "",
  });

  const sortedCertificatesOptions = useMemo(() => {
    return (
      fieldOptions.certificates?.sort((a, b) => a.name.localeCompare(b.name)) ||
      []
    );
  }, [fieldOptions.certificates]);

  const sortedTrainingOptions = useMemo(() => {
    return (
      fieldOptions.trainings?.sort((a, b) => a.name.localeCompare(b.name)) || []
    );
  }, [fieldOptions.trainings]);

  const onInputChange = debounce((key: string, term: string) => {
    setSearchTerms({ ...searchTerms, [key]: term });

    if (!term) {
      setFieldOptions({ ...fieldOptions });
    }
  }, 400);

  const formattedData: DataItem[] =
    languages
      .map((language: LanguageSkill) => ({
        values: {
          language: getLanguageSkillsNameByCode(language.languageCode),
          level: t("languageSkill:" + language.skillLevel),
        },
        id: language.languageCode,
        deletable: true,
      }))
      .sort((a: any, b: any) =>
        a.values.language.localeCompare(b.values.language),
      ) || [];

  const headers = [
    { text: t("languageSkill:language") },
    { text: t("languageSkill:level") },
  ];
  const addButtonLabel = t("languageSkill:add");

  const handleOpenEditModal = (content: ContentKey, id: string = "") => {
    setEditMode({ enabled: true, content, id });
    setSelectedLanguage(
      languages.find((language: Language) => language.languageCode === id),
    );
  };

  const handleCloseEditModal = () => {
    setEditMode({ enabled: false, content: null, id: "" });
    setSelectedLanguage(undefined);
  };

  const onChangeLanguageSkillLevel = (
    languageCode: LanguageSkillCode,
    level: string,
  ) => {
    const editedLanguages = languages.map((language: LanguageSkill) =>
      language.languageCode === languageCode
        ? { ...language, skillLevel: level }
        : language,
    );
    if (
      !languages.find(
        (language: LanguageSkill) => language.languageCode === languageCode,
      )
    ) {
      editedLanguages.push({ languageCode, skillLevel: level });
    }
    setValue("languages", editedLanguages, { shouldDirty: true });
    handleCloseEditModal();
  };

  const deleteLanguage = (id: string) => {
    const editedLanguages = languages.filter(
      (language: LanguageSkill) => language.languageCode !== id,
    );
    setValue("languages", editedLanguages, { shouldDirty: true });
  };

  return (
    <FormSection title={t("common:requirements")}>
      <FormTitleGrid container item xs={4}>
        <Typography variant="secondary">{t("cardsAndCerts")}</Typography>
      </FormTitleGrid>
      <Grid item xs={8} display="flex">
        <Controller
          name="certificates"
          control={control}
          render={({ field }) => (
            <CustomAutocompleteMulti
              name="certificates"
              disabled={isReadOnly}
              defaultValue={certificates}
              options={sortedCertificatesOptions}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_: any, newValue: AutocompleteType[]) => {
                field.onChange(newValue);
              }}
              onInputChange={onInputChange}
              fullWidth
            />
          )}
        />
      </Grid>
      <FormTitleGrid container item xs={4}>
        <Typography variant="secondary">{t("trainings")}</Typography>
      </FormTitleGrid>
      <Grid item xs={8} display="flex">
        <Controller
          name="trainings"
          control={control}
          render={({ field }) => (
            <CustomAutocompleteMulti
              name="trainings"
              disabled={isReadOnly}
              defaultValue={trainings}
              options={sortedTrainingOptions}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_: any, newValue: AutocompleteType[]) => {
                field.onChange(newValue);
              }}
              onInputChange={onInputChange}
              fullWidth
            />
          )}
        />
      </Grid>
      <FormTitleGrid container item xs={4}>
        <Typography variant="secondary">{t("languageSkills")}</Typography>
      </FormTitleGrid>
      <Grid item xs={8}>
        <Controller
          name="languages"
          control={control}
          render={({ field }) => (
            <>
              <FormTable
                data={formattedData}
                headers={headers}
                handleOpenEditModal={handleOpenEditModal}
                contentKey="languages"
                handleDelete={deleteLanguage}
                addButtonLabel={addButtonLabel}
                disableEditRow={isReadOnly}
              />
              <DialogComponent
                open={editMode.enabled}
                onClose={handleCloseEditModal}
                sx={{ maxWidth: "360px" }}
              >
                <LanguageSkillEdit
                  selectedLanguage={selectedLanguage}
                  disabledLanguageCodes={existingLanguageCodes}
                  onSaveChanges={onChangeLanguageSkillLevel}
                  onClose={handleCloseEditModal}
                />
              </DialogComponent>
            </>
          )}
        />
      </Grid>
    </FormSection>
  );
};
